/**
 * First value with non-whitespace content, trimmed. Blank and whitespace-only strings fall
 * through, which is why callers must not reach for nullish coalescing on these fields.
 */
export const firstNonBlankValue = (...values: (string | null | undefined)[]): string | null => {
    for (const value of values) {
        const trimmed = value?.trim();
        if (trimmed) {
            return trimmed;
        }
    }
    return null;
};

/**
 * Provider and internal identifiers that can be shown as the application / loan ID.
 * Intentionally a structural type so both LoanApplicationRead and decision payloads can pass it.
 */
export interface ApplicationDisplayIdFields {
    externalLoanId?: string | null;
    externalPrequalId?: string | null;
    id?: string | null;
}

/** Which field the shown identifier came from, so each screen can label it in its own terms. */
export type ApplicationDisplayIdSource = 'loan' | 'prequal' | 'internal';

export interface ApplicationDisplayId {
    value: string;
    source: ApplicationDisplayIdSource;
}

/**
 * Resolves the identifier to show for a stored loan application: provider loan ID, then
 * provider prequal ID, then the internal id.
 *
 * `source` names the field the value came from, not the phase of the application. Decision
 * screens copy the prequal id onto `externalLoanId` (see useFinancingPrequalSubmission), so
 * they decide their own label from setup data instead of using this.
 */
export const getApplicationDisplayId = ({
    externalLoanId,
    externalPrequalId,
    id,
}: ApplicationDisplayIdFields): ApplicationDisplayId | null => {
    const loanId = firstNonBlankValue(externalLoanId);
    if (loanId) {
        return { value: loanId, source: 'loan' };
    }

    const prequalId = firstNonBlankValue(externalPrequalId);
    if (prequalId) {
        return { value: prequalId, source: 'prequal' };
    }

    const internalId = firstNonBlankValue(id);
    if (internalId) {
        return { value: internalId, source: 'internal' };
    }

    return null;
};
